/**
 * THE PROJECT'S WHITEBOARD — what is on it, and the geometry of drawing it (owner, 2026-10-29: 跟 Miro.com 一樣的
 * 這種白板功能, 可以在上面拖拉一些圖形, 然後製造流程圖或者是上面貼上文字 … 裡面所有參與的使用者以及 AI 都可以來操作).
 *
 * The element shape is the chat service's (chat lambda/boards.ts; the contract is board/CONTRACT in the chat docs):
 * one row per element, written property by property, last writer wins per property. Everything here is pure, so the
 * geometry a line takes is the same on every screen that draws it.
 *
 * Board coordinates are pixels at 100 % zoom; x grows right, y grows down, (x, y) is an element's top-left corner.
 */

export type Kind = 'sticky' | 'shape' | 'text' | 'line' | 'frame' | 'pen' | 'image'
export type ShapeKind =
  | 'rect'
  | 'round'
  | 'pill'
  | 'ellipse'
  | 'diamond'
  | 'parallelogram'
  | 'cylinder'
  | 'triangle'
  | 'hexagon'
  | 'document'
export type Side = 'top' | 'right' | 'bottom' | 'left'
/**
 * A line's end: on an element — anywhere (`id`: the side facing the other end), a side's middle (`side`), or a point
 * along that side (`t`: 0 at its left/top end, 1 at its right/bottom; owner, 2026-10-30: Miro lets an end hold
 * anywhere on the edge) — or a free point.
 */
export type End = { id: string; side?: Side; t?: number } | { x: number; y: number }
export type Route = 'elbow' | 'straight' | 'curve'
export type Align = 'left' | 'center' | 'right'

export type El = {
  id: string
  t: Kind
  x: number
  y: number
  w: number
  h: number
  z: number
  text?: string
  fs?: number
  align?: Align
  bold?: boolean
  color?: string
  shape?: ShapeKind
  fill?: string
  stroke?: string
  sw?: number
  dash?: boolean
  a?: End
  b?: End
  route?: Route
  head?: 'none' | 'arrow'
  tail?: 'none' | 'arrow'
  pts?: number[]
  /** An image's picture: a chat attachment of this room (`uploads/<conv>/<uuid>`), claimed for the board. */
  src?: string
  /** An image's file name — what an agent reading the board sees of it. */
  name?: string
  // kept by the service
  v?: number
  rev?: number
  by?: string
  via?: string
  ts?: number
  del?: boolean
}

/** What a client may write. `null` takes a property away (undo of "it had none"). */
export type Props = { [K in Exclude<keyof El, 'id' | 'v' | 'rev' | 'by' | 'via' | 'ts' | 'del'>]?: El[K] | null }
export type Op = { id: string; set?: Props; del?: true; restore?: true }

/** What a client may write — never the service's own v, rev, by, via, ts, del (and never undone). */
const WRITABLE: Array<keyof Props> = [
  't', 'x', 'y', 'w', 'h', 'z', 'text', 'fs', 'align', 'bold', 'color', 'shape', 'fill', 'stroke', 'sw', 'dash',
  'a', 'b', 'route', 'head', 'tail', 'pts', 'src', 'name',
]

// ── colours ─────────────────────────────────────────────────────────────────

/** The palette the service also knows by name (a note's colours). The board is paper in both themes: one look for all. */
export const NOTE_COLOURS: Array<{ name: string; hex: string }> = [
  { name: 'yellow', hex: '#fff9b1' },
  { name: 'orange', hex: '#ffd8a8' },
  { name: 'red', hex: '#ffc9c9' },
  { name: 'pink', hex: '#fcc2d7' },
  { name: 'purple', hex: '#e5dbff' },
  { name: 'blue', hex: '#d0ebff' },
  { name: 'cyan', hex: '#c5f6fa' },
  { name: 'green', hex: '#d3f9d8' },
  { name: 'gray', hex: '#e9ecef' },
  { name: 'white', hex: '#ffffff' },
]
/** Strong colours, for lines, borders and text. */
export const INK_COLOURS: Array<{ name: string; hex: string }> = [
  { name: 'black', hex: '#1e1e1e' },
  { name: 'gray', hex: '#868e96' },
  { name: 'red', hex: '#e03131' },
  { name: 'orange', hex: '#f08c00' },
  { name: 'green', hex: '#2f9e44' },
  { name: 'blue', hex: '#1c7ed6' },
  { name: 'purple', hex: '#7048e8' },
  { name: 'pink', hex: '#d6336c' },
]
export const INK = '#1e1e1e'
/** Miro's selection blue — what is chosen, and what a line may attach to. */
export const SELECT = '#4262ff'

// ── what a new element starts as ────────────────────────────────────────────

export const SHAPES: Array<{ kind: ShapeKind; label: string }> = [
  { kind: 'rect', label: 'Rectangle' },
  { kind: 'round', label: 'Rounded rectangle' },
  { kind: 'pill', label: 'Start / end' },
  { kind: 'ellipse', label: 'Circle' },
  { kind: 'diamond', label: 'Decision' },
  { kind: 'parallelogram', label: 'Input / output' },
  { kind: 'cylinder', label: 'Database' },
  { kind: 'document', label: 'Document' },
  { kind: 'hexagon', label: 'Preparation' },
  { kind: 'triangle', label: 'Triangle' },
]

export function shapeSize(kind: ShapeKind): { w: number; h: number } {
  switch (kind) {
    case 'diamond':
      return { w: 180, h: 120 }
    case 'ellipse':
      return { w: 120, h: 120 }
    case 'pill':
      return { w: 180, h: 72 }
    case 'triangle':
      return { w: 140, h: 120 }
    case 'cylinder':
      return { w: 140, h: 120 }
    default:
      return { w: 180, h: 96 }
  }
}

export const STICKY_SIZE = 200

const ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789'
/** 10 random base36 characters — the contract's client-minted id. */
export function newId(): string {
  const bytes = new Uint8Array(10)
  crypto.getRandomValues(bytes)
  let s = ''
  for (const b of bytes) s += ID_CHARS[b % 36]
  return s
}

// ── reading the board ───────────────────────────────────────────────────────

export type Box = { x: number; y: number; w: number; h: number }
export type Pt = { x: number; y: number }

export function isAttached(e: End | undefined): e is { id: string; side?: Side; t?: number } {
  return !!e && 'id' in e && typeof e.id === 'string'
}

/** Can a line hold on to this element? Everything that has an outline. */
export function attachable(el: El): boolean {
  return el.t === 'shape' || el.t === 'sticky' || el.t === 'text' || el.t === 'frame' || el.t === 'image'
}

/**
 * Does a corner drag keep this element's proportions? A picture's, so it is never stretched; anything else only with
 * Shift. A note reshapes like a box (owner, 2026-11-01: 便條貼 … 長寬比就沒有辦法被拖拉和改變形狀 — it kept its square).
 */
export function keepsShape(el: Pick<El, 't'> | undefined): boolean {
  return el?.t === 'image'
}

/** Can a person type into this element? Everything but a pen stroke and a picture. */
export function writesText(el: Pick<El, 't'>): boolean {
  return el.t !== 'pen' && el.t !== 'image'
}

export function center(b: Box): Pt {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 }
}

export function sidePoint(b: Box, side: Side): Pt {
  switch (side) {
    case 'top':
      return { x: b.x + b.w / 2, y: b.y }
    case 'bottom':
      return { x: b.x + b.w / 2, y: b.y + b.h }
    case 'left':
      return { x: b.x, y: b.y + b.h / 2 }
    case 'right':
      return { x: b.x + b.w, y: b.y + b.h / 2 }
  }
}

const NORMAL: Record<Side, Pt> = { top: { x: 0, y: -1 }, bottom: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } }

/** The side of `b` that faces `toward` — the larger of the two distances decides. */
function facingSide(b: Box, toward: Pt): Side {
  const c = center(b)
  const dx = (toward.x - c.x) / Math.max(1, b.w)
  const dy = (toward.y - c.y) / Math.max(1, b.h)
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left'
  return dy >= 0 ? 'bottom' : 'top'
}

/** The point `t` of the way along a side of the box (left→right on top/bottom, top→bottom on left/right). */
function alongSide(b: Box, side: Side, t: number): Pt {
  switch (side) {
    case 'top':
      return { x: b.x + b.w * t, y: b.y }
    case 'bottom':
      return { x: b.x + b.w * t, y: b.y + b.h }
    case 'left':
      return { x: b.x, y: b.y + b.h * t }
    case 'right':
      return { x: b.x + b.w, y: b.y + b.h * t }
  }
}

/** A shape's outline as a polygon (board coordinates) — the ones whose outline is not their box. */
function outlineOf(el: El): Pt[] | null {
  if (el.t !== 'shape') return null
  const { x, y, w, h } = el
  switch (el.shape) {
    case 'diamond':
      return [{ x: x + w / 2, y }, { x: x + w, y: y + h / 2 }, { x: x + w / 2, y: y + h }, { x, y: y + h / 2 }]
    case 'triangle':
      return [{ x: x + w / 2, y }, { x: x + w, y: y + h }, { x, y: y + h }]
    case 'parallelogram': {
      const s = Math.min(w * 0.2, h * 0.6)
      return [{ x: x + s, y }, { x: x + w, y }, { x: x + w - s, y: y + h }, { x, y: y + h }]
    }
    case 'hexagon': {
      const s = Math.min(w * 0.22, h * 0.5)
      return [{ x: x + s, y }, { x: x + w - s, y }, { x: x + w, y: y + h / 2 }, { x: x + w - s, y: y + h }, { x: x + s, y: y + h }, { x, y: y + h / 2 }]
    }
    default:
      return null
  }
}

/**
 * Where an end on `side` at `t` meets the element as drawn: on its box for box-like things, on the outline for a
 * diamond, a circle and the slanted shapes — the box point pulled in along the ray from the centre. At `t` = 0.5 on
 * a diamond, circle, hexagon or triangle's top this is the same point as the box's middle.
 */
function outlinePoint(el: El, side: Side, t = 0.5): Pt {
  const p = alongSide(el, side, t)
  const c = center(el)
  const dx = p.x - c.x
  const dy = p.y - c.y
  if (!dx && !dy) return p
  if (el.t === 'shape' && el.shape === 'ellipse') {
    const k = 1 / Math.hypot(dx / Math.max(1, el.w / 2), dy / Math.max(1, el.h / 2))
    return { x: c.x + dx * k, y: c.y + dy * k }
  }
  const poly = outlineOf(el)
  if (!poly) return p
  // The ray c + k·(p − c) meets the outline once (every outline here is convex around its centre): the least k > 0.
  let best = Infinity
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!
    const b = poly[(i + 1) % poly.length]!
    const ex = b.x - a.x
    const ey = b.y - a.y
    const den = dx * ey - dy * ex
    if (Math.abs(den) < 1e-9) continue
    const k = ((a.x - c.x) * ey - (a.y - c.y) * ex) / den
    const u = ((a.x - c.x) * dy - (a.y - c.y) * dx) / den
    if (k > 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) best = Math.min(best, k)
  }
  return best === Infinity ? p : { x: c.x + dx * best, y: c.y + dy * best }
}

/**
 * What a line end dropped at `p` on `el` holds on to: well inside it, the element itself (the end then faces the
 * other one); near an edge, that side — at the very point dropped, or at the side's middle when it lands within
 * `snap` board pixels of it, so a centred connection stays one easy gesture.
 */
export function attachAt(el: El, p: Pt, snap: number): { id: string; side?: Side; t?: number } {
  const inner = { x: el.x + el.w * 0.25, y: el.y + el.h * 0.25, w: el.w * 0.5, h: el.h * 0.5 }
  if (inBox(inner, p)) return { id: el.id }
  const side = nearestSide(el, p)
  const across = side === 'top' || side === 'bottom'
  const len = across ? el.w : el.h
  const t = Math.max(0, Math.min(1, across ? (p.x - el.x) / Math.max(1, el.w) : (p.y - el.y) / Math.max(1, el.h)))
  if (Math.abs(t - 0.5) * len <= snap) return { id: el.id, side }
  return { id: el.id, side, t: Math.round(t * 1000) / 1000 }
}

/** The nearest side to a point, for a line dropped on an element. */
function nearestSide(b: Box, p: Pt): Side {
  const d = [
    { s: 'top' as Side, d: Math.abs(p.y - b.y) },
    { s: 'bottom' as Side, d: Math.abs(p.y - (b.y + b.h)) },
    { s: 'left' as Side, d: Math.abs(p.x - b.x) },
    { s: 'right' as Side, d: Math.abs(p.x - (b.x + b.w)) },
  ]
  d.sort((a, b2) => a.d - b2.d)
  return d[0]!.s
}

export type Anchor = { p: Pt; dir: Pt | null }

/**
 * Where a line's end sits and which way it leaves. An end on an element sits mid-side: the side it names, or the side
 * facing the other end. A free end has no direction of its own. An end whose element is gone is `null` — the line
 * is not drawn (clients delete a shape's lines with it; this covers the moment between).
 */
function resolveEnd(end: End | undefined, other: Pt, els: Record<string, El>): Anchor | null {
  if (!end) return null
  if (!isAttached(end)) return Number.isFinite(end.x) && Number.isFinite(end.y) ? { p: { x: end.x, y: end.y }, dir: null } : null
  const el = els[end.id]
  if (!el || el.del) return null
  const side = end.side ?? facingSide(el, other)
  return { p: outlinePoint(el, side, end.side ? (end.t ?? 0.5) : 0.5), dir: NORMAL[side] }
}

function endGuess(end: End | undefined, els: Record<string, El>): Pt | null {
  if (!end) return null
  if (!isAttached(end)) return { x: end.x, y: end.y }
  const el = els[end.id]
  return el && !el.del ? center(el) : null
}

export type LineGeom = { pts: Pt[]; curve: boolean; a: Anchor; b: Anchor }

/** The points a line is drawn through (an elbow's corners; a curve's start, two controls, end). */
export function lineGeom(line: El, els: Record<string, El>): LineGeom | null {
  const ga = endGuess(line.a, els)
  const gb = endGuess(line.b, els)
  if (!ga || !gb) return null
  const a = resolveEnd(line.a, gb, els)
  const b = resolveEnd(line.b, ga, els)
  if (!a || !b) return null
  const route = line.route ?? 'elbow'
  if (route === 'straight') return { pts: [a.p, b.p], curve: false, a, b }
  if (route === 'curve') {
    const d = Math.hypot(b.p.x - a.p.x, b.p.y - a.p.y)
    const k = Math.max(40, d / 3)
    const da = a.dir ?? unit(a.p, b.p)
    const db = b.dir ?? unit(b.p, a.p)
    return {
      pts: [a.p, { x: a.p.x + da.x * k, y: a.p.y + da.y * k }, { x: b.p.x + db.x * k, y: b.p.y + db.y * k }, b.p],
      curve: true,
      a,
      b,
    }
  }
  return { pts: around(elbow(a, b), a, b, obstaclesOf(els)), curve: false, a, b }
}

function unit(from: Pt, to: Pt): Pt {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const d = Math.hypot(dx, dy) || 1
  return { x: dx / d, y: dy / d }
}

const STUB = 24

/** A free end leaves along the larger axis toward the other end. */
function axisDir(from: Pt, to: Pt): Pt {
  const dx = to.x - from.x
  const dy = to.y - from.y
  return Math.abs(dx) >= Math.abs(dy) ? { x: Math.sign(dx) || 1, y: 0 } : { x: 0, y: Math.sign(dy) || 1 }
}

/**
 * An orthogonal route between two ends: out of each end along its side's normal, then across with as few corners as
 * the two directions allow. It never leaves an end backwards through its own shape; walking around the shapes in
 * between is `around`'s.
 */
export function elbow(a: Anchor, b: Anchor): Pt[] {
  const da = a.dir ?? axisDir(a.p, b.p)
  const db = b.dir ?? axisDir(b.p, a.p)
  const A = a.p
  const B = b.p
  const A1 = { x: A.x + da.x * STUB, y: A.y + da.y * STUB }
  const B1 = { x: B.x + db.x * STUB, y: B.y + db.y * STUB }
  const aH = da.x !== 0
  const bH = db.x !== 0
  let mid: Pt[]
  if (aH && bH) {
    const ahead = da.x > 0 ? B1.x >= A1.x : B1.x <= A1.x
    if (da.x === db.x) {
      // both ends face the same way (a loop beside a flowchart): out past the further of the two, along, back in
      const mx = da.x > 0 ? Math.max(A1.x, B1.x) : Math.min(A1.x, B1.x)
      mid = [{ x: mx, y: A.y }, { x: mx, y: B.y }]
    } else if (ahead) {
      const mx = (A1.x + B1.x) / 2
      mid = [{ x: mx, y: A.y }, { x: mx, y: B.y }]
    } else {
      const my = (A1.y + B1.y) / 2
      mid = [A1, { x: A1.x, y: my }, { x: B1.x, y: my }, B1]
    }
  } else if (!aH && !bH) {
    const ahead = da.y > 0 ? B1.y >= A1.y : B1.y <= A1.y
    if (da.y === db.y) {
      const my = da.y > 0 ? Math.max(A1.y, B1.y) : Math.min(A1.y, B1.y)
      mid = [{ x: A.x, y: my }, { x: B.x, y: my }]
    } else if (ahead) {
      const my = (A1.y + B1.y) / 2
      mid = [{ x: A.x, y: my }, { x: B.x, y: my }]
    } else {
      const mx = (A1.x + B1.x) / 2
      mid = [A1, { x: mx, y: A1.y }, { x: mx, y: B1.y }, B1]
    }
  } else if (aH) {
    // out sideways, in from above/below: one corner where the two meet, when both ends are walked forward into it
    const c = { x: B.x, y: A.y }
    const okA = (c.x - A.x) * da.x > 0
    const okB = (c.y - B.y) * db.y > 0
    mid = okA && okB ? [c] : [A1, { x: A1.x, y: B1.y }, B1]
  } else {
    const c = { x: A.x, y: B.y }
    const okA = (c.y - A.y) * da.y > 0
    const okB = (c.x - B.x) * db.x > 0
    mid = okA && okB ? [c] : [A1, { x: B1.x, y: A1.y }, B1]
  }
  return simplify([A, ...mid, B])
}

/** How far a connector keeps from a shape it walks around. */
const CLEAR = 24

/**
 * What an elbow walks around: the notes, shapes and pictures, its own two ends included (a loop between two stacked
 * boxes must not cut through both). Frames hold things and text sits beside lines, so neither is in the way. One list
 * per board picture, shared by all of its lines.
 */
const OBSTACLES = new WeakMap<Record<string, El>, Box[]>()
function obstaclesOf(els: Record<string, El>): Box[] {
  let boxes = OBSTACLES.get(els)
  if (!boxes) {
    boxes = Object.values(els).filter((e) => !e.del && (e.t === 'shape' || e.t === 'sticky' || e.t === 'image'))
    OBSTACLES.set(els, boxes)
  }
  return boxes
}

/** Does an axis-aligned segment pass through the inside of a box? Running along its outline does not count. */
function crosses(p: Pt, q: Pt, b: Box): boolean {
  const e = 1
  return Math.max(p.x, q.x) > b.x + e && Math.min(p.x, q.x) < b.x + b.w - e && Math.max(p.y, q.y) > b.y + e && Math.min(p.y, q.y) < b.y + b.h - e
}

function blockers(pts: Pt[], boxes: Box[]): Box[] {
  return boxes.filter((b) => pts.some((p, i) => i > 0 && crosses(pts[i - 1]!, p, b)))
}

/**
 * An elbow that would pass through a shape takes a lane beside everything in its way instead (owner's flowchart,
 * 2026-10-30: the "no" link of a decision ran straight through the "yes" box an agent's drawing put below it). The
 * lane runs along the flow — left or right of the blockers for an up/down link, above or below them for a sideways
 * one — and widens until it is clear. The shortest clear lane wins; with none, the plain elbow stands.
 */
function around(base: Pt[], a: Anchor, b: Anchor, boxes: Box[]): Pt[] {
  const hit = blockers(base, boxes)
  if (!hit.length) return base
  const A = base[0]!
  const B = base[base.length - 1]!
  const da = a.dir ?? axisDir(A, B)
  const db = b.dir ?? axisDir(B, A)
  const A1 = { x: A.x + da.x * STUB, y: A.y + da.y * STUB }
  const B1 = { x: B.x + db.x * STUB, y: B.y + db.y * STUB }
  let best = base
  let bestCost = Infinity
  for (const upDown of [true, false]) {
    for (const sign of [-1, 1]) {
      let u = union(hit)!
      for (let k = 0; k < 4; k++) {
        const c = upDown ? (sign < 0 ? u.x - CLEAR : u.x + u.w + CLEAR) : sign < 0 ? u.y - CLEAR : u.y + u.h + CLEAR
        const pts = simplify(
          upDown
            ? [A, A1, { x: c, y: A1.y }, { x: c, y: B1.y }, B1, B]
            : [A, A1, { x: A1.x, y: c }, { x: B1.x, y: c }, B1, B],
        )
        const more = blockers(pts, boxes)
        if (!more.length) {
          let cost = (pts.length - 2) * 10 // a corner costs a little length
          for (let i = 1; i < pts.length; i++) cost += dist(pts[i - 1]!, pts[i]!)
          if (cost < bestCost) {
            best = pts
            bestCost = cost
          }
          break
        }
        u = union([u, ...more])!
      }
    }
  }
  return best
}

/** Drop repeated points and the middle of three in a row. */
function simplify(pts: Pt[]): Pt[] {
  const out: Pt[] = []
  for (const p of pts) {
    const last = out[out.length - 1]
    if (last && Math.abs(last.x - p.x) < 0.5 && Math.abs(last.y - p.y) < 0.5) continue
    out.push(p)
  }
  let changed = true
  while (changed && out.length > 2) {
    changed = false
    for (let i = 1; i < out.length - 1; i++) {
      const p = out[i - 1]!
      const q = out[i]!
      const r = out[i + 1]!
      const cross = (q.x - p.x) * (r.y - q.y) - (q.y - p.y) * (r.x - q.x)
      if (Math.abs(cross) < 0.5) {
        out.splice(i, 1)
        changed = true
        break
      }
    }
  }
  return out
}

/** SVG path data for a line's geometry. */
export function linePath(g: LineGeom): string {
  const [p0, ...rest] = g.pts
  if (!p0) return ''
  if (g.curve && rest.length === 3) {
    const [c1, c2, p] = rest as [Pt, Pt, Pt]
    return `M${p0.x},${p0.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${p.x},${p.y}`
  }
  return `M${p0.x},${p0.y}` + rest.map((p) => ` L${p.x},${p.y}`).join('')
}

/** The direction a line arrives at its end (for the arrowhead), and leaves its start. */
export function endTangent(g: LineGeom, which: 'a' | 'b'): Pt {
  const p = g.pts
  if (which === 'b') return unit(p[p.length - 2]!, p[p.length - 1]!)
  return unit(p[1]!, p[0]!)
}

/** The point halfway along a line (where its label sits). */
export function midpoint(g: LineGeom): Pt {
  if (g.curve && g.pts.length === 4) {
    const [p0, c1, c2, p3] = g.pts as [Pt, Pt, Pt, Pt]
    const t = 0.5
    const m = (a: number, b: number, c: number, d: number) =>
      (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t ** 2 * c + t ** 3 * d
    return { x: m(p0.x, c1.x, c2.x, p3.x), y: m(p0.y, c1.y, c2.y, p3.y) }
  }
  let total = 0
  for (let i = 1; i < g.pts.length; i++) total += dist(g.pts[i - 1]!, g.pts[i]!)
  let left = total / 2
  for (let i = 1; i < g.pts.length; i++) {
    const s = dist(g.pts[i - 1]!, g.pts[i]!)
    if (left <= s) {
      const k = s ? left / s : 0
      const a = g.pts[i - 1]!
      const b = g.pts[i]!
      return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k }
    }
    left -= s
  }
  return g.pts[0]!
}

function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y)
}

/** How far a point is from a line as drawn (curves sampled), in board pixels. */
function distanceToLine(g: LineGeom, p: Pt): number {
  const pts: Pt[] = []
  if (g.curve && g.pts.length === 4) {
    const [p0, c1, c2, p3] = g.pts as [Pt, Pt, Pt, Pt]
    for (let i = 0; i <= 24; i++) {
      const t = i / 24
      const m = (a: number, b: number, c: number, d: number) =>
        (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t ** 2 * c + t ** 3 * d
      pts.push({ x: m(p0.x, c1.x, c2.x, p3.x), y: m(p0.y, c1.y, c2.y, p3.y) })
    }
  } else pts.push(...g.pts)
  let best = Infinity
  for (let i = 1; i < pts.length; i++) best = Math.min(best, segDist(pts[i - 1]!, pts[i]!, p))
  return best
}

function segDist(a: Pt, b: Pt, p: Pt): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const l2 = dx * dx + dy * dy
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/** The box an element covers — a line's from its drawn points, everything else its own. */
export function boxOf(el: El, els: Record<string, El>): Box | null {
  if (el.t !== 'line') return { x: el.x, y: el.y, w: el.w, h: el.h }
  const g = lineGeom(el, els)
  if (!g) return null
  return boundsOfPoints(g.pts)
}

function boundsOfPoints(pts: Pt[]): Box {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const p of pts) {
    x0 = Math.min(x0, p.x)
    y0 = Math.min(y0, p.y)
    x1 = Math.max(x1, p.x)
    y1 = Math.max(y1, p.y)
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

export function union(boxes: Box[]): Box | null {
  if (!boxes.length) return null
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const b of boxes) {
    x0 = Math.min(x0, b.x)
    y0 = Math.min(y0, b.y)
    x1 = Math.max(x1, b.x + b.w)
    y1 = Math.max(y1, b.y + b.h)
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

export function contains(outer: Box, inner: Box): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h
}

export function intersects(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

function inBox(b: Box, p: Pt, slack = 0): boolean {
  return p.x >= b.x - slack && p.x <= b.x + b.w + slack && p.y >= b.y - slack && p.y <= b.y + b.h + slack
}

/** Live elements in paint order: frames first (they hold things), then by z. */
export function paintOrder(els: Record<string, El>): El[] {
  return Object.values(els)
    .filter((e) => !e.del)
    .sort((a, b) => (a.t === 'frame') !== (b.t === 'frame') ? (a.t === 'frame' ? -1 : 1) : a.z - b.z || (a.id < b.id ? -1 : 1))
}

export function topZ(els: Record<string, El>): number {
  let z = 0
  for (const e of Object.values(els)) if (!e.del && Number.isFinite(e.z)) z = Math.max(z, e.z)
  return z
}

export function bottomZ(els: Record<string, El>): number {
  let z = 0
  for (const e of Object.values(els)) if (!e.del && Number.isFinite(e.z)) z = Math.min(z, e.z)
  return z
}

/**
 * How far up or down the selection goes (owner, 2026-11-03: 新的元件永遠會排在舊的元件的上面. 我們需要能夠控制深度的操作):
 * to the very top or bottom, or one step — over (under) the next thing it OVERLAPS, so a step always shows on the board
 * (one place in the board's whole order would often pass something far away and change nothing to see).
 */
export type Restack = 'front' | 'forward' | 'backward' | 'back'

/**
 * The writes that restack the selection, or none when it is already there. Frames are a layer of their own under
 * everything (paintOrder), so a frame moves among frames, anything else among the rest; several selected keep their own
 * order and go together. The moved ones take z between their new neighbours; when two neighbours are too close for that
 * (z is a double), that layer is numbered afresh, 0, 1, 2 … — every element in it written, one undo step all the same.
 */
export function restackOps(els: Record<string, El>, sel: string[], how: Restack): Op[] {
  const picked = new Set(sel)
  const ops: Op[] = []
  const order = paintOrder(els)
  for (const frames of [true, false]) {
    const layer = order.filter((e) => (e.t === 'frame') === frames)
    const mine = layer.filter((e) => picked.has(e.id))
    if (!mine.length) continue
    const rest = layer.filter((e) => !picked.has(e.id))
    const pos = new Map(layer.map((e, i) => [e.id, i]))
    const boxes = mine.map((e) => boxOf(e, els)).filter((b): b is Box => !!b)
    const touches = (o: El) => {
      const b = boxOf(o, els)
      return !!b && boxes.some((m) => intersects(m, b))
    }
    // Where the selection lands among the rest: just before rest[at].
    let at: number
    if (how === 'front') at = rest.length
    else if (how === 'back') at = 0
    else if (how === 'forward') {
      const top = pos.get(mine[mine.length - 1]!.id)!
      const over = rest.findIndex((o) => pos.get(o.id)! > top && touches(o))
      if (over < 0) continue
      at = over + 1
    } else {
      const bottom = pos.get(mine[0]!.id)!
      let under = -1
      for (let i = rest.length - 1; i >= 0; i--) {
        if (pos.get(rest[i]!.id)! < bottom && touches(rest[i]!)) {
          under = i
          break
        }
      }
      if (under < 0) continue
      at = under
    }
    const next = [...rest.slice(0, at), ...mine, ...rest.slice(at)]
    if (next.every((e, i) => e === layer[i])) continue
    const lo = at > 0 ? rest[at - 1]!.z : null
    const hi = at < rest.length ? rest[at]!.z : null
    const n = mine.length
    const zs = mine.map((_, i) => (hi === null ? lo! + i + 1 : lo === null ? hi - n + i : lo + ((hi - lo) * (i + 1)) / (n + 1)))
    const room = zs.every((z, i) => Number.isFinite(z) && z > (i ? zs[i - 1]! : (lo ?? -Infinity)) && z < (hi ?? Infinity))
    if (room) mine.forEach((e, i) => ops.push({ id: e.id, set: { z: zs[i]! } }))
    else next.forEach((e, i) => e.z !== i && ops.push({ id: e.id, set: { z: i } }))
  }
  return ops
}

/** The lines holding on to any of these elements. */
export function linesOf(ids: Set<string>, els: Record<string, El>): El[] {
  return Object.values(els).filter(
    (e) => !e.del && e.t === 'line' && ((isAttached(e.a) && ids.has(e.a.id)) || (isAttached(e.b) && ids.has(e.b.id))),
  )
}

/**
 * The topmost element under a point that a click should pick: not a frame's inside (a frame is picked by its title
 * or border — clicking inside one starts a selection box, as in Miro), lines by a few pixels of slack.
 */
export function hitTest(els: Record<string, El>, p: Pt, slackPx: number, opts: { attachOnly?: boolean; skip?: Set<string> | undefined } = {}): El | null {
  const order = paintOrder(els).reverse()
  for (const el of order) {
    if (opts.skip?.has(el.id)) continue
    if (opts.attachOnly && !attachable(el)) continue
    if (el.t === 'line') {
      if (opts.attachOnly) continue
      const g = lineGeom(el, els)
      if (g && distanceToLine(g, p) <= Math.max(6, slackPx)) return el
      continue
    }
    if (el.t === 'frame' && !opts.attachOnly) {
      const title = { x: el.x, y: el.y - 28, w: Math.max(80, Math.min(el.w, 320)), h: 28 }
      const edge = inBox(el, p, slackPx) && !inBox({ x: el.x + slackPx, y: el.y + slackPx, w: el.w - 2 * slackPx, h: el.h - 2 * slackPx }, p)
      if (inBox(title, p) || edge) return el
      continue
    }
    if (inBox(el, p)) return el
  }
  return null
}

// ── what a write changes, and how to take it back ───────────────────────────

/**
 * The ops that undo `ops`, read against the board as it was BEFORE them. A create is undone by a delete; a delete by
 * a restore carrying everything the element had; a change by writing each named property back (absent → null).
 */
export function inverseOps(before: Record<string, El>, ops: Op[]): Op[] {
  const out: Op[] = []
  for (const op of [...ops].reverse()) {
    const prev = before[op.id]
    if (op.del) {
      if (prev && !prev.del) out.push({ id: op.id, restore: true, set: writable(prev) })
      continue
    }
    if (!prev || prev.del) {
      out.push({ id: op.id, del: true })
      continue
    }
    const set: Props = {}
    for (const k of Object.keys(op.set ?? {}) as Array<keyof Props>) {
      if (k === 't') continue
      const v = (prev as Record<string, unknown>)[k]
      ;(set as Record<string, unknown>)[k] = v === undefined ? null : v
    }
    if (Object.keys(set).length) out.push({ id: op.id, set })
  }
  return out
}

/** Everything about an element a client may write (its own properties, none of the service's). */
export function writable(el: El): Props {
  const out: Props = {}
  for (const k of WRITABLE) {
    const v = (el as Record<string, unknown>)[k]
    if (v !== undefined) (out as Record<string, unknown>)[k] = v
  }
  return out
}

/** One element with a change applied (null removes). */
export function withProps<E extends { id: string } = El>(el: E, set: object): E {
  const next: Record<string, unknown> = { ...el }
  for (const [k, v] of Object.entries(set)) {
    if (k === 'id') continue
    if (v === null) delete next[k]
    else next[k] = v
  }
  return next as E
}

// ── pictures ────────────────────────────────────────────────────────────────

/** A picture's size on the board: its own, scaled down (never up) to fit `maxW`×`maxH`, whole pixels. */
export function fitImage(w: number, h: number, maxW: number, maxH: number): { w: number; h: number } {
  const k = Math.min(1, maxW / Math.max(1, w), maxH / Math.max(1, h))
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) }
}

/** Several pictures dropped at once: side by side, `gap` apart, the row centred on `at`, tops aligned. */
export function rowAt(sizes: Array<{ w: number; h: number }>, at: Pt, gap: number): Box[] {
  const total = sizes.reduce((n, s) => n + s.w, 0) + gap * Math.max(0, sizes.length - 1)
  const tall = Math.max(0, ...sizes.map((s) => s.h))
  let x = at.x - total / 2
  return sizes.map((s) => {
    const b = { x: Math.round(x), y: Math.round(at.y - tall / 2), w: s.w, h: s.h }
    x += s.w + gap
    return b
  })
}

/**
 * How far to move `box` so it stands clear of everything on the board (lines and frames aside): not at all when it is
 * clear already, else the nearest of a few steps around it — right, down, left, up, then the corners — growing out.
 * With no clear spot near, it stays where it was asked to go.
 */
export function clearSpot(box: Box, els: Record<string, El>, gap = 40): Pt {
  const solid = Object.values(els).filter((e) => !e.del && e.t !== 'line' && e.t !== 'frame')
  const taken = (b: Box) => solid.some((e) => intersects(b, e))
  if (!taken(box)) return { x: 0, y: 0 }
  const dirs = [[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]] as const
  for (let r = 1; r <= 6; r++) {
    for (const [dx, dy] of dirs) {
      const off = { x: dx * r * (box.w + gap), y: dy * r * (box.h + gap) }
      if (!taken({ ...box, x: box.x + off.x, y: box.y + off.y })) return off
    }
  }
  return { x: 0, y: 0 }
}

/** The sizes a note's words step down through when they do not fit at its own size (ElementView's layoutText). */
export const STICKY_STEPS = [48, 40, 32, 28, 24, 20, 18, 16, 14, 12, 10, 8]
